/**
 * Admin patch-notes queries — CRUD over patch_notes + patch_note_items,
 * plus the user-seen tracking reads/writes used by the changelog modal.
 */
import { supabase } from '@/lib/supabase'

/** Load patch notes with items (admin view — all statuses). */
export async function fetchAllPatchNotes() {
  const { data, error } = await supabase
    .from('patch_notes')
    .select('*, patch_note_items(*)')
    .order('sort_order', { ascending: false })
  if (error) throw error
  return data || []
}

/** Load live patch notes with items (public changelog). */
export async function fetchLivePatchNotes() {
  const { data, error } = await supabase
    .from('patch_notes')
    .select('*, patch_note_items(*)')
    .eq('status', 'live')
    .order('sort_order', { ascending: false })
  if (error) throw error
  return data || []
}

/** Load patch notes for update modal (admin_only + live). */
export async function fetchVisiblePatchNotes() {
  const { data, error } = await supabase
    .from('patch_notes')
    .select('*, patch_note_items(*)')
    .in('status', ['admin_only', 'live'])
    .order('sort_order', { ascending: false })
  if (error) throw error
  return data || []
}

/** Release a patch note (change status to live). */
export async function releasePatchNote(id) {
  const { error } = await supabase
    .from('patch_notes')
    .update({ status: 'live', released_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}

/**
 * Create a new patch note with items.
 *
 * New notes default to the TOP of the changelog. We compute `MAX(sort_order) + 1`
 * instead of using `Date.now()` directly because the column is `integer`
 * (max 2,147,483,647) and `Date.now()` in ms is ~1.78 trillion — overflows the
 * column and silently falls back to the default (`0`), which lands the note at
 * the bottom. MAX + 1 is bulletproof regardless of column type.
 */
export async function createPatchNote({ version, title, items }) {
  const { data: maxRow, error: maxErr } = await supabase
    .from('patch_notes')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (maxErr) throw maxErr
  const newSortOrder = (maxRow?.sort_order ?? 0) + 1

  const { data, error } = await supabase
    .from('patch_notes')
    .insert({ version, title, status: 'admin_only', sort_order: newSortOrder })
    .select()
    .single()
  if (error) throw error
  if (items?.length) {
    const rows = items.map((item, i) => ({
      patch_note_id: data.id,
      category: item.category,
      description: item.description,
      sort_order: i,
    }))
    const { error: itemErr } = await supabase.from('patch_note_items').insert(rows)
    if (itemErr) throw itemErr
  }
  return data
}

/** Update a patch note's title/version + replace its items. */
export async function updatePatchNote(id, { version, title, items }) {
  const { error } = await supabase.from('patch_notes').update({ version, title }).eq('id', id)
  if (error) throw error
  // Replace items: delete existing, insert new
  const { error: delErr } = await supabase.from('patch_note_items').delete().eq('patch_note_id', id)
  if (delErr) throw delErr
  if (items?.length) {
    const rows = items.map((item, i) => ({
      patch_note_id: id,
      category: item.category,
      description: item.description,
      sort_order: i,
    }))
    const { error: itemErr } = await supabase.from('patch_note_items').insert(rows)
    if (itemErr) throw itemErr
  }
}

/** Delete a patch note and its items. */
export async function deletePatchNote(id) {
  // Items cascade via FK or delete manually
  const { error: itemErr } = await supabase
    .from('patch_note_items')
    .delete()
    .eq('patch_note_id', id)
  if (itemErr) throw itemErr
  // Delete seen records
  const { error: seenErr } = await supabase
    .from('user_seen_patch_notes')
    .delete()
    .eq('patch_note_id', id)
  if (seenErr) throw seenErr
  const { error } = await supabase.from('patch_notes').delete().eq('id', id)
  if (error) throw error
}

/** Load which patch notes a user has seen. */
export async function fetchSeenPatchNotes(userId) {
  const { data, error } = await supabase
    .from('user_seen_patch_notes')
    .select('patch_note_id')
    .eq('user_id', userId)
  if (error) throw error
  return (data || []).map(r => r.patch_note_id)
}

/** Mark patch notes as seen by a user. */
export async function markPatchNotesSeen(rows) {
  const { error } = await supabase.from('user_seen_patch_notes').upsert(rows)
  if (error) throw error
}

/**
 * Swap the `sort_order` of two patch notes.
 *
 * Used by the admin reorder UI to move a note up or down in the changelog
 * display order. The page sorts notes DESC by sort_order, so swapping with
 * the row above moves a note up; swapping with the row below moves it down.
 */
export async function swapPatchNoteSortOrders(id1, id2) {
  const { data, error: fetchErr } = await supabase
    .from('patch_notes')
    .select('id, sort_order')
    .in('id', [id1, id2])
  if (fetchErr) throw fetchErr
  const a = data.find(n => n.id === id1)
  const b = data.find(n => n.id === id2)
  if (!a || !b) throw new Error('patch notes not found')
  // Two serial updates — race risk is negligible in single-admin tooling.
  const { error: e1 } = await supabase
    .from('patch_notes')
    .update({ sort_order: b.sort_order })
    .eq('id', id1)
  if (e1) throw e1
  const { error: e2 } = await supabase
    .from('patch_notes')
    .update({ sort_order: a.sort_order })
    .eq('id', id2)
  if (e2) throw e2
}
